/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { Link } from 'react-router-dom'

import { font, paragraphXSmall, weightRegular } from '../../css'
import MapMarkerIcon from '../../icons/MapMarkerIcon'
import { Flex } from '../../Layout'
import GameListRowTeam from './GameListRowTeam'
import GameListRowTime from './GameListRowTime'
import Text from 'src/components/ts-components/Text'

const Score = ({ children, isWinner }) => {
  return (
    <Flex
      column
      alignItems='center'
      css={css`
        flex-basis: 25%;
        ${font.title}
        font-weight: 700;
        font-size: 64px;
        letter-spacing: 0.015em;
      `}
    >
      <span>
        {children}
        {isWinner && (
          <div
            className='score-indicator'
            css={css`
              height: 4px;
              background-color: ${colors.DEFAULT_FLAIR};
            `}
          />
        )}
      </span>
    </Flex>
  )
}

const GameListRow = ({
  game,
  gameTypes,
  gameStatusTypes,
  hasNextSibling,
  isSoccer = false,
  isHockey = false,
  isFirstGame = false,
  onClickFirstGame = () => {},
  scheduleId = null,
  isMySchedule = false,
  isLastGame = false,
}) => {
  const fullGameType = gameTypes?.find((gameType) => {
    return parseInt(gameType?.id, 10) === game?.game_type_id
  })

  const homeTeamWon =
    game.home_team_score > game.visiting_team_score || game.game_status_id === 3
  const visitingTeamWon =
    game.home_team_score < game.visiting_team_score || game.game_status_id === 2

  const isGenericSport = game?.generic_sport_id !== null

  return (
    <Flex
      alignItems='stretch'
      justifyContent='space-between'
      className='game-list-row'
      css={css`
        margin-left: -8px;
        margin-right: -8px;
        min-height: ${hasNextSibling ? 102 : 92}px;
        ${hasNextSibling ? 'border-bottom: 1px solid #323A48; padding-bottom: 16px;' : ''}
        ${hasNextSibling &&
        `
          margin-bottom: 16px;
        `}
      `}
    >
      <GameListRowTeam
        team={game.homeTeam}
        isHome
        teamSlot={game.homeTeamSlot}
        scheduleId={isMySchedule ? game.rootSchedule?.id || null : scheduleId}
      />
      <Flex
        onClick={async () => {
          if (isFirstGame) {
            await onClickFirstGame()
          }
        }}
        id={isFirstGame ? 'sandbox-schedule-games' : ''}
        as={Link}
        to={`${ROUTES.GAME_ROOT}/${game.id}`}
        row
        alignItems='center'
        justifyContent='center'
        css={css`
          width: 50%;
          height: inherit;
          padding: 8px;
          transition: background-color 0.2s ease-in-out;
          &:hover {
            background-color: ${colors.SECONDARY_300};
            border-radius: 8px;
          }
        `}
      >
        <Score isWinner={homeTeamWon}>{game.home_team_score}</Score>
        <Flex
          column
          css={css`
            flex-basis: 50%;
            text-align: center;
            font-weight: 700;
            font-size: 12px;
            line-height: 12px;
            color: #9fa3a9;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              gap: 8px;
            `}
          >
            <Text
              color={colors.NEUTRAL_50}
              variant='captionXSmall'
              weight='semiBold'
              style={`text-transform: uppercase; margin-left: ${
                isMySchedule ? '2px' : '1px'
              }`}
            >
              {isMySchedule ? game.rootSchedule?.name : game.schedule.name}
            </Text>
            <Text
              color={colors.NEUTRAL_50}
              variant='captionXSmall'
              weight='semiBold'
              style={`text-transform: uppercase;`}
            >
              {game?.league_game_number
                ? `Game #${game?.league_game_number} - `
                : ''}
              {fullGameType?.name}{' '}
              {game.if_necessary_status && (
                <span
                  css={css`
                    text-transform: lowercase;
                  `}
                >
                  (if Necessary)
                </span>
              )}
            </Text>
          </div>

          <GameListRowTime
            game={game}
            gameStatusTypes={gameStatusTypes}
            isSoccer={isSoccer}
            isHockey={isHockey}
            isGenericSport={isGenericSport}
          />

          <div
            css={css`
              margin-top: 4px;
            `}
          >
            <MapMarkerIcon size={14} color={colors.NEUTRAL_50} />
            <p
              css={css`
                ${paragraphXSmall}
                ${weightRegular}
                color: ${colors.NEUTRAL_50};
                display: inline-block;
                margin-left: 4px;
              `}
            >
              {game?.venue?.name}
              {game.facility ? ` - ${game.facility.name}` : ''}
            </p>
          </div>
        </Flex>
        <Score isWinner={visitingTeamWon}>{game.visiting_team_score}</Score>
      </Flex>
      <GameListRowTeam
        team={game.visitingTeam}
        teamSlot={game.visitingTeamSlot}
        scheduleId={isMySchedule ? game.rootSchedule?.id || null : scheduleId}
      />
    </Flex>
  )
}

export default GameListRow
